import { useEffect, useState } from 'react';
import { CheckCircle2, Info, AlertTriangle, XCircle, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastKind = 'success' | 'danger' | 'warning' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
}

/* ---------------------------------------------------------------------------
 * Imperative toast — feedback that survives navigation, replacing the
 * per-screen `const [msg, setMsg] = useState(...)` pattern and window.alert().
 *
 *   toast.success('Invite sent.');
 *   toast.danger('Could not disconnect', e.message);
 *
 * A single <ToastViewport /> mounted in App renders the stack.
 * ------------------------------------------------------------------------- */

let nextId = 1;
let dispatch: ((t: ToastItem) => void) | null = null;

function push(kind: ToastKind, title: string, description?: string) {
  dispatch?.({ id: nextId++, kind, title, description });
}

export const toast = {
  success: (title: string, description?: string) => push('success', title, description),
  danger: (title: string, description?: string) => push('danger', title, description),
  warning: (title: string, description?: string) => push('warning', title, description),
  info: (title: string, description?: string) => push('info', title, description),
};

const icons: Record<ToastKind, JSX.Element> = {
  info: <Info className="h-4 w-4" aria-hidden />,
  success: <CheckCircle2 className="h-4 w-4" aria-hidden />,
  warning: <AlertTriangle className="h-4 w-4" aria-hidden />,
  danger: <XCircle className="h-4 w-4" aria-hidden />,
};

const tones: Record<ToastKind, string> = {
  info: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

/** Mount once at the app root. */
export function ToastViewport() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    dispatch = (t) => {
      setToasts((prev) => [...prev.slice(-3), t]);
      // Errors linger longer so they can be read; successes clear themselves.
      const ttl = t.kind === 'danger' ? 8000 : 4500;
      setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== t.id)), ttl);
    };
    return () => {
      dispatch = null;
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className="pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border border-border bg-surface px-3 py-2.5 shadow-pop"
        >
          <div className={cn('mt-0.5 shrink-0', tones[t.kind])}>{icons[t.kind]}</div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium leading-snug text-fg">{t.title}</p>
            {t.description && <p className="mt-0.5 text-xs text-fg-muted">{t.description}</p>}
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            className="shrink-0 text-fg-subtle hover:text-fg"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}
